import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import PageTitle from '../../components/page-title/PageTitle';
import { Container } from './FollowupList.styled';
import { useDiseaseClassificationService } from '../../hooks/services/useDiseaseClassificationService';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import IDiseaseClassification from '../../interfaces/IDiseaseClassification';
import { useValidateSchema } from '../../hooks/providers/useValidateSchema';
import IFormError from '../../interfaces/IFormError';
import debounce from 'lodash.debounce';
import * as yup from 'yup';
import { AiFillCloseCircle } from 'react-icons/ai';
import { useMedicalCertificateService } from '../../hooks/services/useMedicalCertificateService';
import AppError from '../../errors/AppError';

interface IProps {
  closeDialog: () => void;
  isOpen: boolean;
  medicalCertificateId: string;
  handleRequest: () => Promise<void>;
}

const FollowupDiseaseClassificationForm = (props: IProps) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly IDiseaseClassification[]>([]);
  const loadingInput = open;

  const [disease, setDisease] = useState<IDiseaseClassification | null>(null);
  const request = useDiseaseClassificationService();
  const medicalCertificateRequest = useMedicalCertificateService();
  const toastr = useToastrVanila();
  const validation = useValidateSchema();
  const [errors, setErrors] = useState<IFormError | null>();

  useEffect(() => {
    let active = true;

    if (!loadingInput) {
      return undefined;
    }

    (async () => {
      if (active) {
        await loadDiseases('');
      }
    })();

    return () => {
      active = false;
    };
  }, [loadingInput]);

  useEffect(() => {
    setErrors(null);
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const create = async (disease: IDiseaseClassification): Promise<void> => {
    setLoading(true);
    medicalCertificateRequest
      .createDiseasesClassification(disease)
      .then(async () => {
        setLoading(false);
        toastr.success('Registro incluído com sucesso');
        props.closeDialog();
        await props.handleRequest();
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
        throw new AppError(error.message);
      });
  };

  const loadDiseases = debounce(async (filter = '') => {
    setLoading(true);
    await request
      .list(filter)
      .then(diseaseClassification => {
        setOptions([...diseaseClassification, {}]);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(`Erro ao carregar os dados: ${error.message}`);
        setLoading(false);
      });
  }, 500);

  const schema = yup.object({
    code: yup.string().required('Campo obrigatório').min(1, 'Campo obrigatório.'),
  });

  const handleSaveClick = async () => {
    const result = await validation.validate(schema, disease || {});
    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }
    await create({
      ...disease,
      medicalCertificateId: props.medicalCertificateId,
    });
  };

  return (
    <Container>
      <PageTitle title="ADICIONAR CID">
        <Tooltip title="Fechar edição de CID" placement="left">
          <IconButton onClick={() => props.closeDialog()}>
            <AiFillCloseCircle />
          </IconButton>
        </Tooltip>
      </PageTitle>
      {loading && <LinearProgress />}
      <form>
        <Autocomplete
          open={open}
          value={disease}
          onChange={(e: any, newValue) => {
            const castedDisease = { ...newValue } as IDiseaseClassification;
            setDisease({ ...castedDisease });
          }}
          onOpen={() => {
            setOpen(true);
            setDisease(null);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option, value) => {
            return option?.description === value?.description;
          }}
          getOptionLabel={option => (option?.code ? `${option.code} - ${option.description}` : '')}
          options={options}
          loading={loading}
          renderInput={params => (
            <TextField
              {...params}
              sx={{ marginTop: '30px' }}
              label="CID "
              error={Boolean(errors?.code)}
              helperText={errors?.code}
              onChange={e => {
                loadDiseases(e.target.value);
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </form>
      <Box sx={{ padding: '50px 0px 10px 0px' }}>
        <Button variant="contained" color="secondary" onClick={() => props.closeDialog()}>
          Cancelar
        </Button>
        <Button variant="contained" sx={{ marginLeft: 2 }} onClick={() => handleSaveClick()}>
          Salvar
        </Button>
      </Box>
    </Container>
  );
};

export default FollowupDiseaseClassificationForm;
